import { useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useMount } from 'react-use'
import * as R from 'remeda'
import { match, P } from 'ts-pattern'
import { z } from 'zod'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { surveyGroupFilterAtom } from '~/atoms'
import { LoadingPanel } from '~/components/elements/LoadingPanel'
import { ErrorPanel } from '~/components/features/ErrorPanel'
import { graphql } from '~/gql'
import { checkCurrentBrandAccount } from '~/hoc/checkCurrentBrandAccount'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useTabulate } from '~/hooks/useTabulate'
import {
  conditionOperations,
  exists,
  mapItemsToObj,
  surveyGroupFilterGroupsSchema,
  toSurveyGroupFilterItemInputGroups,
  toSurveyGroupFilterV2,
} from '~/utils'

const countSurveyGroupTotalAnswersMutationDocument = graphql(`
  mutation RestoreSurveyGroupFilterPage_CountSurveyGroupTotalAnswersMutation(
    $surveyGroupId: String!
    $filter: [[SurveyGroupCustomerAttributeFilterItemInput!]!]!
  ) {
    count: countSurveyGroupTotalAnswers(
      surveyGroupId: $surveyGroupId
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)

const countSurveyGroupTotalAnswersQueryDocument = graphql(`
  query RestoreSurveyGroupFilterPage_CountSurveyGroupTotalAnswers(
    $awaitId: String!
  ) {
    count: countSurveyGroupTotalAnswers(awaitId: $awaitId) {
      arrived
      result {
        surveyId
        count
      }
    }
  }
`)

const surveyGroupQueryDocument = graphql(`
  query RestoreSurveyGroupFilterPage_SurveyGroup($surveyGroupId: ID!) {
    surveyGroup(surveyGroupId: $surveyGroupId) {
      id
      questions {
        id
        answerOptions {
          option
          text
        }
      }
    }
  }
`)

type RestoringState = 'complete' | 'invalidParams' | 'noParams' | 'processing'

const schema = z.object({
  replace: z.object({ path: z.string(), state: z.unknown() }),
  surveyGroupFilter: z.union([
    z.object({
      surveyGroupId: z.string(),
      filterGroups: surveyGroupFilterGroupsSchema,
    }),
    z.object({
      surveyGroupId: z.string(),
      filterGroups: z.undefined(),
      questionFilters: z
        .array(
          z.object({
            surveyGroupQuestionId: z.string(),
            operation: z.enum(conditionOperations),
            values: z.array(z.string()),
          }),
        )
        .optional()
        .default([]),
      segmentFilters: z
        .array(
          z.object({
            surveyGroupBrandId: z.string(),
            segmentNumber: z.number(),
          }),
        )
        .optional()
        .default([]),
    }),
  ]),
})

const backLinkPath = compile('top', {})

export const RestoreSurveyGroupFilterPage = checkCurrentBrandAccount(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const [restoringState, setRestoringState] =
    React.useState<RestoringState>('processing')

  const graphQLRequest = useGraphQLRequest()

  const [_countSurveyGroupTotalAnswersState, countSurveyGroupTotalAnswers] =
    useTabulate(
      countSurveyGroupTotalAnswersMutationDocument,
      countSurveyGroupTotalAnswersQueryDocument,
    )

  const setSurveyGroupFilter = useSetAtom(surveyGroupFilterAtom)

  useMount(async () => {
    const state = location.state

    if (!exists(state)) {
      setRestoringState('noParams')

      return
    }

    const parsed = schema.safeParse(state)
    if (!parsed.success) {
      setRestoringState('invalidParams')

      return
    }

    const { replace, surveyGroupFilter } = parsed.data

    const surveyGroupId = surveyGroupFilter.surveyGroupId

    const filterGroups = await match(surveyGroupFilter)
      .with(
        {
          filterGroups: P.not(P.nullish),
        },
        ({ filterGroups }) => filterGroups,
      )
      .with(
        {
          questionFilters: P.not(P.nullish),
          segmentFilters: P.not(P.nullish),
        },
        async (surveyFilter) => {
          const data = await graphQLRequest({
            document: surveyGroupQueryDocument,
            variables: { surveyGroupId },
          })
          const questions = data.surveyGroup?.questions ?? []

          return toSurveyGroupFilterV2(surveyFilter, questions)
        },
      )
      .exhaustive()

    if (0 < filterGroups.length) {
      const result = await countSurveyGroupTotalAnswers({
        surveyGroupId,
        filter: toSurveyGroupFilterItemInputGroups(filterGroups),
      })

      setSurveyGroupFilter({
        countMap: mapItemsToObj(result ?? [], R.prop('count'), 'surveyId'),
        filterGroups,
      })
    } else {
      setSurveyGroupFilter(undefined)
    }

    navigate(replace.path, { state: replace.state, replace: true })

    setRestoringState('complete')
  })

  return match(restoringState)
    .with('processing', () => <LoadingPanel containerSize="full" />)
    .with('noParams', () => (
      <ErrorPanel
        backLinkPath={backLinkPath}
        message={t('this_page_cannot_be_accessed_directly')}
      />
    ))
    .with('invalidParams', () => (
      <ErrorPanel
        backLinkPath={backLinkPath}
        message={t('invalid_access_detected')}
      />
    ))
    .with('complete', () => null)
    .exhaustive()
})
